import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Save, Loader2, Heart, Target, Globe, Banknote, Clock, Mic, MicOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const moduleConfig = {
  passions: { icon: Heart, color: '#FF6B6B', gradient: 'from-[#FF6B6B] to-[#FF8E8E]' },
  talents: { icon: Target, color: '#FFE66D', gradient: 'from-[#FFE66D] to-[#FFF3A3]' },
  utility: { icon: Globe, color: '#4ECDC4', gradient: 'from-[#4ECDC4] to-[#7BE0D9]' },
  monetization: { icon: Banknote, color: '#1A535C', gradient: 'from-[#1A535C] to-[#2D7A86]' }
};

const moduleMotivation = {
  passions: {
    fr: { banner: 'Ce qui te fait vibrer est la clé de ton avenir.', completion: 'Tes passions sont maintenant capturées. L\'IA va pouvoir révéler ce que tu ne vois pas encore.' },
    en: { banner: 'What makes you come alive is the key to your future.', completion: 'Your passions are now captured. The AI will reveal what you can\'t yet see.' }
  },
  talents: {
    fr: { banner: 'Tes talents naturels valent de l\'or. Identifions-les ensemble.', completion: 'Tes forces sont uniques. Tu es plus proche que jamais de ton Ikigai.' },
    en: { banner: 'Your natural talents are worth gold. Let\'s identify them together.', completion: 'Your strengths are unique. You\'re closer than ever to your Ikigai.' }
  },
  utility: {
    fr: { banner: 'Le monde a besoin de ce que toi seul peux apporter.', completion: 'Tu sais maintenant comment tu peux faire la différence. La pièce du puzzle se met en place.' },
    en: { banner: 'The world needs what only you can bring.', completion: 'You now know how you can make a difference. The puzzle is coming together.' }
  },
  monetization: {
    fr: { banner: 'Vivre de ce qui te passionne, c\'est possible. Dernière étape.', completion: 'Toutes les pièces sont en place. Ton Ikigai est prêt à être révélé par l\'IA.' },
    en: { banner: 'Making a living from your passion is possible. Final step.', completion: 'All the pieces are in place. Your Ikigai is ready to be revealed by AI.' }
  }
};

const Module = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const { t, language } = useLanguage();
  
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const wantsListeningRef = useRef(false);

  const config = moduleConfig[moduleId] || moduleConfig.passions;
  const Icon = config.icon;

  // Speech Recognition setup
  const SpeechRecognition = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const speechSupported = !!SpeechRecognition;

  const stopListening = () => {
    wantsListeningRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    setIsListening(false);
  };

  const startOneShot = (qId) => {
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = language === 'fr' ? 'fr-FR' : 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onresult = (event) => {
      const text = event.results[0]?.[0]?.transcript?.trim();
      if (text) {
        setAnswers(prev => {
          const current = prev[qId] || '';
          const sep = current && !current.endsWith(' ') ? ' ' : '';
          return { ...prev, [qId]: current + sep + text };
        });
      }
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech' && wantsListeningRef.current) {
        // Silence detected, restart to keep listening
        startOneShot(qId);
        return;
      }
      if (event.error !== 'aborted') {
        toast.error(language === 'fr' ? 'Erreur micro. Vérifie les permissions.' : 'Mic error. Check permissions.');
      }
      wantsListeningRef.current = false;
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      // Auto-restart if user hasn't pressed stop
      if (wantsListeningRef.current) {
        startOneShot(qId);
      } else {
        setIsListening(false);
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
      return;
    }

    const qId = questions[currentIndex]?.id;
    if (!qId) return;

    wantsListeningRef.current = true;
    setIsListening(true);
    startOneShot(qId);
  };

  // Cleanup on question change or unmount
  useEffect(() => {
    return () => {
      wantsListeningRef.current = false;
      if (recognitionRef.current) { recognitionRef.current.abort(); recognitionRef.current = null; }
    };
  }, [currentIndex]);

  const fetchQuestions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/modules/${moduleId}/questions`);
      setQuestions(response.data);
    } catch (e) {
      console.warn('Module questions:', e.message);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [moduleId, language, navigate]);

  const fetchSavedResponses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/modules/${moduleId}/responses`);
      const savedAnswers = {};
      response.data.forEach(r => {
        savedAnswers[r.question_id] = r.answer;
      });
      setAnswers(savedAnswers);
    } catch (e) { console.warn('Saved responses:', e.message); }
  }, [moduleId]);

  useEffect(() => {
    fetchQuestions();
    fetchSavedResponses();
  }, [fetchQuestions, fetchSavedResponses]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleSaveAndFinish = async () => {
    setSaving(true);
    try {
      const responses = Object.entries(answers).map(([question_id, answer]) => ({
        module_id: moduleId,
        question_id,
        answer
      }));

      await axios.post(`${API}/modules/${moduleId}/responses`, {
        module_id: moduleId,
        responses
      });

      await fetchUser();
      const completionMsg = moduleMotivation[moduleId]?.[language]?.completion;
      toast.success(completionMsg || (language === 'fr' ? 'Module complété !' : 'Module completed!'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de sauvegarde' : 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="flex items-center gap-3 text-stone-600">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>{t('loading')}</span>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const answeredCount = Object.keys(answers).filter(k => answers[k]?.trim()).length;

  const moduleTitles = {
    passions: { fr: 'Passions', en: 'Passions' },
    talents: { fr: 'Talents', en: 'Talents' },
    utility: { fr: 'Utilité', en: 'Utility' },
    monetization: { fr: 'Monétisation', en: 'Monetization' }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">{language === 'fr' ? 'Retour' : 'Back'}</span>
          </button>
          
          <div className="flex items-center gap-2">
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${config.color}20` }}
            >
              <Icon className="w-4 h-4" style={{ color: config.color }} />
            </div>
            <span className="font-serif font-semibold text-stone-800">
              {moduleTitles[moduleId]?.[language] || moduleId}
            </span>
          </div>
          
          <div className="text-sm text-stone-500">
            {currentIndex + 1}/{questions.length}
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="sticky top-[72px] z-40 px-6 py-2 bg-[#FAFAF9]/80 backdrop-blur">
        <div className="max-w-4xl mx-auto">
          <Progress value={progress} className="h-2 bg-stone-100" />
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Motivational Banner */}
        <div 
          className="mb-8 px-5 py-4 rounded-2xl border flex items-center gap-3"
          style={{ backgroundColor: `${config.color}08`, borderColor: `${config.color}20` }}
        >
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${config.color}15` }}
          >
            <Icon className="w-4 h-4" style={{ color: config.color }} />
          </div>
          <p className="text-sm text-stone-600 italic leading-relaxed">
            {moduleMotivation[moduleId]?.[language]?.banner}
          </p>
        </div>

        {/* Question Card */}
        <div className="card-ikigai animate-fade-in" key={currentIndex}>
          {/* Question Number & Duration */}
          <div className="flex items-center justify-between mb-6">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
              style={{ backgroundColor: `${config.color}10`, color: config.color }}
            >
              Question {currentIndex + 1}
            </div>
            {currentQuestion?.duration && (
              <div className="flex items-center gap-2 text-stone-500 text-sm">
                <Clock className="w-4 h-4" />
                <span>{currentQuestion.duration}</span>
              </div>
            )}
          </div>
          
          {/* Question Text */}
          <h2 className="font-serif text-2xl md:text-3xl font-semibold text-stone-900 mb-8 leading-relaxed">
            {currentQuestion?.question}
          </h2>
          
          {/* Answer Textarea with Voice Input */}
          <div className="relative">
            <Textarea
              value={answers[currentQuestion?.id] || ''}
              onChange={(e) => handleAnswerChange(currentQuestion?.id, e.target.value)}
              placeholder={currentQuestion?.placeholder || t('answerPlaceholder')}
              className={`input-ikigai min-h-[200px] text-lg resize-none pr-14 ${isListening ? 'ring-2 ring-[#FF6B6B]/40' : ''}`}
              data-testid={`answer-${currentQuestion?.id}`}
            />
            {speechSupported && (
              <button
                type="button"
                onClick={toggleListening}
                className={`absolute bottom-4 right-4 w-11 h-11 rounded-full flex items-center justify-center transition-all shadow-md ${
                  isListening 
                    ? 'bg-[#FF6B6B] text-white animate-pulse shadow-[#FF6B6B]/30' 
                    : 'bg-white text-stone-400 hover:text-[#FF6B6B] hover:shadow-lg border border-stone-200'
                }`}
                data-testid="voice-input-btn"
                title={isListening 
                  ? (language === 'fr' ? 'Arrêter la dictée' : 'Stop dictation')
                  : (language === 'fr' ? 'Dicter ma réponse' : 'Dictate my answer')}
              >
                {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
            )}
          </div>
          
          {/* Status + Character count */}
          <div className="mt-2">
            <div className="flex items-center justify-between">
              {isListening && (
                <p className="text-sm text-[#FF6B6B] font-medium flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#FF6B6B] animate-pulse" />
                  {language === 'fr' ? 'Parle, j\'écoute...' : 'Speak, I\'m listening...'}
                </p>
              )}
              {!isListening && speechSupported && (
                <p className="text-xs text-stone-300">
                  {language === 'fr' ? 'Appuie sur le micro pour dicter' : 'Tap the mic to dictate'}
                </p>
              )}
              {!speechSupported && <div />}
              <p className="text-sm text-stone-400">
                {(answers[currentQuestion?.id] || '').length} {language === 'fr' ? 'caractères' : 'characters'}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <Button
            onClick={handlePrevious}
            variant="outline"
            className="btn-secondary flex items-center gap-2"
            disabled={currentIndex === 0}
            data-testid="previous-question-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">{t('previousQuestion')}</span>
          </Button>

          <div className="text-center">
            <span className="text-sm text-stone-500">
              {answeredCount}/{questions.length} {language === 'fr' ? 'répondu' : 'answered'}
            </span>
          </div>

          {currentIndex < questions.length - 1 ? (
            <Button
              onClick={handleNext}
              className="btn-primary flex items-center gap-2"
              data-testid="next-question-btn"
            >
              <span className="hidden sm:inline">{t('nextQuestion')}</span>
              <ArrowRight className="w-5 h-5" />
            </Button>
          ) : (
            <Button
              onClick={handleSaveAndFinish}
              className="btn-primary flex items-center gap-2"
              disabled={saving || answeredCount === 0}
              data-testid="save-finish-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="hidden sm:inline">{language === 'fr' ? 'Sauvegarde...' : 'Saving...'}</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span className="hidden sm:inline">{t('saveAndFinish')}</span>
                </>
              )}
            </Button>
          )}
        </div>

        {/* Quick navigation dots */}
        <div className="flex justify-center gap-2 mt-8 flex-wrap">
          {questions.map((q, index) => (
            <button
              key={q.id}
              onClick={() => setCurrentIndex(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentIndex 
                  ? 'scale-125' 
                  : answers[q.id] 
                    ? 'bg-stone-400' 
                    : 'bg-stone-200'
              }`}
              style={index === currentIndex ? { backgroundColor: config.color } : {}}
              data-testid={`question-dot-${index}`}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default Module;
